import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { RELEASE_NOTES as FALLBACK_NOTES } from "@/lib/releaseNotes";
import { api } from "@/lib/api";
import { Sparkles, ArrowUpRight } from "lucide-react";

/**
 * WhatsNewModal — Customer-facing release feed.
 *
 * v77.99.22 — Customer / internal split.
 *
 * The public `GET /api/release-notes` endpoint now returns ONLY
 * `audience == "customer"` rows. This component therefore drops all
 * of the developer-changelog scaffolding (tone icon, version stamp,
 * "ops" colour-coding) and replaces it with a polished SaaS-style
 * product feed:
 *
 *   • Grouped by month (e.g. "May 2026") so updates feel anchored
 *     to a time rather than a build number.
 *   • Each card surfaces: optional NEW pill, optional category badge
 *     (Expenses / Privacy / Mobile / …), plain-English title,
 *     optional one-line description, optional bullet list, optional
 *     screenshot thumbnail, optional "Learn more →" link.
 *   • The technical metadata (version, tone, ops/refactor markers)
 *     is intentionally NOT shown — that's the whole point of the
 *     audience split. Operators see versions in the admin console;
 *     parents do not.
 *
 * Tone (visual):
 *   • Calm, reassuring, scannable.
 *   • No icons inside each entry — clutter for a product feed.
 *   • Month headers are the only structural element; everything else
 *     reads top-to-bottom like a polished changelog.
 */

const CATEGORY_STYLE = {
  // Each category gets a soft tinted pill. Defaults to neutral.
  Expenses:      { bg: "bg-[#E7F4F3]", text: "text-[#0F6B6E]", border: "border-[#A7D8DA]" },
  Calendar:      { bg: "bg-[#EFF1FB]", text: "text-[#3F4FA3]", border: "border-[#C7CFEC]" },
  Messages:      { bg: "bg-[#F1F4F1]", text: "text-[#3F6147]", border: "border-[#CBDDD2]" },
  Requests:      { bg: "bg-[#FBF3E7]", text: "text-[#8B6A23]", border: "border-[#E8D6A4]" },
  Journal:       { bg: "bg-[#F5EEF7]", text: "text-[#5C3D6D]", border: "border-[#D8C7E1]" },
  Documents:     { bg: "bg-[#EEF1F4]", text: "text-[#46545F]", border: "border-[#CCD5DC]" },
  Children:      { bg: "bg-[#FEF1F3]", text: "text-[#8A2F44]", border: "border-[#EDC4CD]" },
  Privacy:       { bg: "bg-[#EAF6EE]", text: "text-[#235D38]", border: "border-[#B9DAC4]" },
  Mobile:        { bg: "bg-[#EEF6FC]", text: "text-[#2A5C84]", border: "border-[#BFD9EE]" },
  Accessibility: { bg: "bg-[#F4F0FB]", text: "text-[#4A3878]", border: "border-[#CFC4E7]" },
  Performance:   { bg: "bg-[#FBF4EA]", text: "text-[#7A551B]", border: "border-[#E8CFA0]" },
  Exports:       { bg: "bg-[#EEF6F3]", text: "text-[#1F6A57]", border: "border-[#B9DACD]" },
  Notifications: { bg: "bg-[#FBEEF1]", text: "text-[#8F2944]", border: "border-[#E8C4CE]" },
};

const NEUTRAL_PILL = {
  bg: "bg-[#F4F6F7]", text: "text-[#46545F]", border: "border-[#D9E1E3]",
};

const MONTH_FMT = new Intl.DateTimeFormat("en-GB", { month: "long", year: "numeric" });

function monthKey(iso) {
  if (!iso || iso.length < 7) return "Other";
  return iso.slice(0, 7); // YYYY-MM
}

function monthLabel(key) {
  const [y, m] = key.split("-");
  if (!y || !m) return key;
  const dt = new Date(Number(y), Number(m) - 1, 1);
  return MONTH_FMT.format(dt);
}

export function WhatsNewModal({ open, onOpenChange }) {
  const [notes, setNotes] = useState(FALLBACK_NOTES);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/release-notes");
        if (!cancelled && Array.isArray(data?.notes) && data.notes.length > 0) {
          setNotes(data.notes);
        }
      } catch {
        // Keep the bundled fallback so the modal never renders empty.
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  // Group notes by month (YYYY-MM). Preserves the newest-first order
  // returned by the API so the first group is the most recent month.
  const grouped = useMemo(() => {
    const map = new Map();
    notes.forEach((n) => {
      const k = monthKey(n.date);
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(n);
    });
    return Array.from(map.entries());
  }, [notes]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-white border-[#D9E1E3] max-h-[85vh] overflow-y-auto sm:max-w-xl"
        data-testid="whats-new-modal"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#1F9FA4]" /> What&apos;s new
          </DialogTitle>
        </DialogHeader>
        <p className="text-[13px] text-[#5C6E76] leading-relaxed">
          Small, calm improvements to make CoParent Console easier and
          safer to use. The newest changes are at the top.
        </p>

        {notes.length === 0 ? (
          <div className="mt-6 text-sm text-[#6F828A] italic" data-testid="whats-new-empty">
            No updates yet — check back soon.
          </div>
        ) : (
          <div className="mt-4 space-y-6" data-testid="whats-new-list">
            {grouped.map(([key, rows]) => (
              <section key={key} className="space-y-3">
                <h3 className="text-[11px] uppercase tracking-[0.14em] text-[#6F828A] font-medium">
                  {monthLabel(key)}
                </h3>
                <ul className="space-y-3">
                  {rows.map((rel) => (
                    <ReleaseCard key={rel.id || `${rel.date}-${rel.title}`} rel={rel} />
                  ))}
                </ul>
              </section>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ReleaseCard({ rel }) {
  const catStyle = (rel.category && CATEGORY_STYLE[rel.category]) || NEUTRAL_PILL;
  const bodyLines = Array.isArray(rel.body)
    ? rel.body
    : (rel.body ? [rel.body] : []);
  return (
    <li
      className="rounded-md border border-[#E2E8EC] p-4 hover:border-[#C7D6DA] transition-colors"
      data-testid={`whats-new-card-${rel.id || rel.version}`}
    >
      <div className="flex items-center gap-2 flex-wrap">
        {rel.highlight && (
          <span
            className="inline-flex items-center text-[10px] font-semibold uppercase tracking-[0.14em] px-1.5 py-0.5 rounded-sm bg-[#1F9FA4] text-white"
            data-testid="whats-new-new-pill"
          >
            New
          </span>
        )}
        {rel.category && (
          <span
            className={`inline-flex items-center text-[10.5px] uppercase tracking-[0.1em] px-2 py-0.5 border rounded-sm ${catStyle.bg} ${catStyle.text} ${catStyle.border}`}
            data-testid={`whats-new-category-${rel.category}`}
          >
            {rel.category}
          </span>
        )}
      </div>

      <div className="mt-2 font-heading text-[17px] font-semibold text-[#1A2A2E] leading-snug">
        {rel.title}
      </div>

      {rel.description && (
        <p className="mt-1.5 text-[14px] text-[#3F5057] leading-relaxed">
          {rel.description}
        </p>
      )}

      {bodyLines.length > 0 && (
        <ul className="mt-2 text-[13.5px] text-[#3F5057] list-disc pl-5 space-y-1">
          {bodyLines.map((line, i) => (
            // Body lines are an immutable static list parsed from the
            // release note content — never reordered/filtered, so the
            // index is a stable key. Including the release id in the
            // key prefix keeps it unique across multiple releases
            // rendered on the same page.
            <li key={`${rel.id}-line-${i}`}>{line}</li>
          ))}
        </ul>
      )}

      {rel.screenshot_url && (
        <div className="mt-3">
          <img
            src={rel.screenshot_url}
            alt={rel.title}
            loading="lazy"
            className="rounded-sm border border-[#E2E8EC] max-h-48 w-auto"
            data-testid="whats-new-screenshot"
          />
        </div>
      )}

      {rel.learn_more_url && (
        <a
          href={rel.learn_more_url}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-3 inline-flex items-center gap-1 text-[13px] font-medium text-[#1F9FA4] hover:text-[#178085]"
          data-testid="whats-new-learn-more"
        >
          Learn more <ArrowUpRight className="w-3.5 h-3.5" />
        </a>
      )}
    </li>
  );
}

export default WhatsNewModal;
